import { AggregationFunction } from "@multiweb/clients/dataReader";
import { getDataReaderClient } from "@multiweb/clients/util";
import { Event } from "../../../../../Global/Base/Events";
import { StringUtils } from "../../../../../Global/Base/Utils/StringUtils";
import { AttrValueReader } from "../../../../../OW.Remoting/Remoting/AttrValueReader";
import { OWObject } from "../../../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { OWManager } from "../../../../../OW.Remoting/Remoting/OWManager";
import { ChartType } from "../../../../UI.Controls/ChartElement";
import { IChartColumnDefinition } from "../../../../UI.Controls/DataGridSubcontrols/Columns/ColumnFactory";
import { UIManager } from "../../UIManager";
import { IChartProfileInfo } from "../Forms/IFormProfileInfo";
import { GenericListFrame } from "./GenericListFrame";

/**
 * obsahuje parametry pro tvorbu grafu
 * v ChartSettingsPanelu se parametry pro tvorbu grafu nastavují
 * v ChartFramu se pak graf podle parametrů zobrazuje
 */
export class ChartSettingsManager {
    /*
     * událost vyvolaná změnou parametru pro tvorbu grafu
     * na základě ní se pak překreslí zobrazení grafu
     */
    public settingsChanged: Event<ChartSettingsManager, IChartProfileInfo> = new Event<ChartSettingsManager, IChartProfileInfo>();
    /* událost vyvolaná načtením uloženého grafu */
    public profileLoaded: Event<ChartSettingsManager, IChartProfileInfo> = new Event<ChartSettingsManager, IChartProfileInfo>();
    /**
     * vyvolaná změnou agregační funkce
     * první část pole je nová hodnota agregační funkce (AggregationFunction), druhá id datasetu kam agregační funkce spadá
     */
    public aggregationFunctionChanged: Event<string[], void> = new Event<string[], void>();
    /**
    * vyvolaná změnou lokalizačního popisku ovládacího prvku
    * první část pole je nová hodnota lokalizačního popisku, druhá id datasetu kam lokalizační popisek spadá
    */
    public captionChanged: Event<string[], void> = new Event<string[], void>();
    /**
    * vyvolaná změnou zobrazovaného atributu
    * první část pole je nová hodnota zobrazovaného atributu, druhá id datasetu kam zobrazovaný atribut spadá
    */
    public attrNameChanged: Event<string[], void> = new Event<string[], void>();
    /**
     * vyvolaná klikem na smazat
     */
    public deleteDatasetClicked: Event<string[], void> = new Event<string[], void>();
    /**
     * vyvolaná změnou typu grafu
     */
    public xAxisAttrChanged: Event<string, void> = new Event<ChartType, void>();
    /**
     * vyvolaná změnou typu grafu v hlavičce
     */
    public chartTypeChanged: Event<ChartType, void> = new Event<ChartType, void>();
    /**
     * vyvolaná změnou orientace grafu
     */
    public chartOrientationChanged: Event<boolean, void> = new Event<boolean, void>();
    /**
     * vyvolaná změnou typu grafu v Zobrazení
     */
    public chartTypeChangedFromDisplay: Event<ChartType, void> = new Event<ChartType, void>();
    /**
     * vyvolaná změnou zobrazení legendy grafu
     */
    public showLegendChanged: Event<boolean, void> = new Event<boolean, void>();

    private _settings: IChartProfileInfo;
    private readonly _formName: string;
    private _showInNavigationTree: boolean = false;
    private _profileID: string;
    private readonly _masterFrame: GenericListFrame;
    private _isImplementationChart: boolean = false;
    private _chartProfileInfoEvents: Event<string[], void>[] = [];
    private readonly _currentUserId: number | null;

    public get chartSettings(): IChartProfileInfo {
        return this._settings;
    }
    public set chartSettings(v: IChartProfileInfo) {
        this._settings = v;
    }

    public get showInNavigationTree(): boolean {
        return this._showInNavigationTree;
    }
    public set showInNavigationTree(v: boolean) {
        this._showInNavigationTree = v;
    }

    public get isImplementationChart(): boolean {
        return this._isImplementationChart;
    }
    public set isImplementationChart(v: boolean) {
        this._isImplementationChart = v;
    }

    public get masterFrame(): GenericListFrame {
        return this._masterFrame;
    }

    constructor(formName: string, masterFrame: GenericListFrame, currentUserId: number | null) {
        this._formName = formName;
        this._masterFrame = masterFrame;
        this._currentUserId = currentUserId;

        this._setListeners();
    }

    private _setListeners(): void {
        this._chartProfileInfoEvents.push(this.aggregationFunctionChanged);
        this._chartProfileInfoEvents.push(this.captionChanged);
        this._chartProfileInfoEvents.push(this.attrNameChanged);
        this._chartProfileInfoEvents.push(this.deleteDatasetClicked);
        for (let i = 0; i < this._chartProfileInfoEvents.length; i++) {
            this._chartProfileInfoEvents[i].add(value => this._runChartProfileInfoEventsWithDataset(value, this._chartProfileInfoEvents[i]));
        }

        const events = [
            this.xAxisAttrChanged,
            this.chartTypeChanged,
            this.chartOrientationChanged,
            this.showLegendChanged,
        ];
        for (const eventItem of events) {
            eventItem.add(value => this._runChartProfileInfoEvents(value, eventItem));
        }
    }

    private _runChartProfileInfoEventsWithDataset(value: string[], chartProfileInfoEvent: Event<string[], void>): void {
        const item = value[0];
        const datasetID = value[1];
        const dataset = this.getDataSet(datasetID);
        switch (chartProfileInfoEvent) {
            case this.aggregationFunctionChanged:
                dataset.aggregateFunction = item as AggregationFunction;
                break;
            case this.captionChanged:
                dataset.caption = item;
                break;
            case this.attrNameChanged:
                dataset.attrName = item;
                break;
            case this.deleteDatasetClicked:
                this._settings.dataSets.forEach((item, index) => {
                    if (item === dataset) {
                        this._settings.dataSets.splice(index, 1);
                    }
                });
                break;
        }
        this.settingsChanged.trigger(this, this.chartSettings);
    }

    private _runChartProfileInfoEvents(value: any, chartProfileInfoEvent: Event<any, void>): void {
        switch (chartProfileInfoEvent) {
            case this.xAxisAttrChanged:
                this.chartSettings.xAxisAttr = value;
                break;
            case this.chartTypeChanged:
                this.chartSettings.chartType = value;
                break;
            case this.chartOrientationChanged:
                this.chartSettings.horizontal = value;
                break;
            case this.showLegendChanged:
                this.chartSettings.showLegend = value;
                break;
        }
        this.settingsChanged.trigger(this, this.chartSettings);
    }

    /**
     * získá dataset
     * pokud dataset neexistuje, vytvoří jej
     */
    public getDataSet(datasetID: string): IChartColumnDefinition {
        let dataset = this.chartSettings.dataSets.find((dataset) => dataset.id === datasetID);
        if (dataset) {
            return dataset;
        } else {
            dataset = new ChartColumnDefinition();
            dataset.id = datasetID;
            this.chartSettings.dataSets.push(dataset);
            return dataset;
        }
    }

    /**
    * uložení uživatelsky vytvořeného grafu
    */
    public async saveSeparateProfile(): Promise<void> {
        UIManager.instance.showLoader();
        let userProfile: OWObject;
        try {
            if (StringUtils.isNullOrEmpty(this._profileID)) {
                userProfile = await OWManager.newObject("UserProfile", undefined);
            } else {
                userProfile = await OWManager.getObject(this._profileID, "UserProfile", undefined);
            }

            userProfile.setValue("User", this._isImplementationChart ? 0 : this._currentUserId);
            userProfile.setValue("Name", this._settings.chartName);
            userProfile.setValue("IsDefault", null);
            userProfile.setValue("FormName", this._formName);
            userProfile.setValue("ShowInNavigationTree", this._showInNavigationTree);

            const jsonData = JSON.stringify(this._settings);
            userProfile.setValue("Data", jsonData);
            userProfile.setValue("DataType", "ChartProfileMW");
            await userProfile.waitForUpdateInstance();
            await userProfile.applyUpdates();

            this._profileID = userProfile.id;
        } finally {
            userProfile.dispose();
            UIManager.instance.hideLoader();
        }
    }

    /**
     * Načtení uživatelsky vytvořeného grafu
     * @param profileID id profilu z tabulky UserProfile
     */
    public async loadSeparateProfile(profileID: string): Promise<void> {
        this._profileID = profileID;

        const chartProfiles = await getDataReaderClient().getDataRowsWithParams("UserProfile", ["Data", "User"],
            ":UserProfile.ActualUserProfiles() AND ID = @0",
            { params: [profileID], dataLength: -1 });

        if (chartProfiles.length > 0) {
            const chartProfileJsonData = AttrValueReader.read(chartProfiles[0]["Data"]);
            this._settings = JSON.parse(chartProfileJsonData || null) || {};
            this.isImplementationChart = (AttrValueReader.read(chartProfiles[0]["User"]) === 0);

            if (this.profileLoaded) {
                this.profileLoaded.trigger(this, this._settings);
            }
        }
    }
}

/** pokud dataset pro svislou osu neexistuje, je třeba vytvořit nový */
export class ChartColumnDefinition implements IChartColumnDefinition {
    public aggregateFunction?: AggregationFunction;
    public condition?: string;
    public chartColor?: string;
    public chartType?: ChartType;
    public showAxisName?: boolean;
    public showAxisValue?: boolean;
    public showChartValue?: boolean;
    public caption?: string;
    public attrName: string = "ID";
    public dataType?: string;
    public fill?: boolean;
    public id?: string;
    public isEditable?: boolean;
    public visible?: boolean;
    public locKey?: string;
    public width?: number;
    public displayFormat?: string;
    public orderBy?: string;
    public culture?: string;
    public showAttrs?: string[];
    public compactArea?: string;
    public compactAreaOrder?: number;
    public fileMask?: string;
    public editWindow?: string;
    public lovStyle?: string;
    public conditionParams?: string;
    public enumName?: string;
    public allowsignwithoutedit?: string;
    public allowCounterSignature?: string;
}
